// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import ShoppingBag from "@insite/mobius/Icons/ShoppingBag";
import ShoppingCart from "@insite/mobius/Icons/ShoppingCart";
import Truck from "@insite/mobius/Icons/Truck";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { FloatingTooltipStyles } from "@steiner/App/Components/FloatingTooltip/FloatingTooltip";
import HeaderClickable, { HeaderClickableStyles } from "@steiner/App/Components/Header/HeaderClickable";
import FloatingMenuTooltip, {
    DEFAULT_STEINER_LARGE_STICKY_OFFSET,
    DEFAULT_STEINER_OFFSET,
} from "@steiner/App/Components/Menu/FloatingMenuTooltip";
import MiniCart from "@steiner/App/Components/MiniCart/MiniCart";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
}

const mapStateToProps = (state: CustomAppState) => ({
    shouldLoadCart: !getCurrentCartState(state).value && !getCurrentCartState(state).isLoading,
    totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
    cartItemsCount: getCurrentCartState(state).value?.cartLines?.length || 0,
    cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
    currency: getSession(state).currency?.currencySymbol,
    orderSubTotalDisplay: getCurrentCartState(state).value?.orderSubTotalDisplay,
    /**
     * XNG-Changes
     */
    isCartPage: getCurrentPage(state).type === "CartPage",
    isTabletScreen: state.xCommon.isTabletScreen,
    isSticky: state.xHeader.isSticky,
});

const mapDispatchToProps = {
    loadCurrentCart,
};

export interface CartLinkStyles {
    routerLink?: LinkPresentationProps;
    desktopCountText?: TypographyPresentationProps;
    mobileCountText?: TypographyPresentationProps;
    compactCountText?: TypographyPresentationProps;
    // XNG-Changes
    headerClickable?: HeaderClickableStyles;
    floatingTooltip?: FloatingTooltipStyles;
    subtitle?: TypographyPresentationProps;
    count?: TypographyPresentationProps;
}

export const cartLinkStyles: CartLinkStyles = {
    routerLink: {
        typographyProps: {
            variant: "headerSecondary",
        },
        icon: {
            iconProps: {
                size: 24,
            },
        },
        color: "secondary.contrast",
        css: css`
            height: 50px;
            padding: 0 4px 0 10px;
        `,
    },
    compactCountText: {
        css: css`
            font-weight: normal;
        `,
    },
};

const iconMap = {
    cart: ShoppingCart,
    truck: Truck,
    bag: ShoppingBag,
    none: undefined,
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: keyof typeof iconMap;
    };
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay: number | undefined;

const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    totalCountDisplay,
    loadCurrentCart,
    currency,
    cartItemsCount,
    orderSubTotalDisplay,
    isCartPage,
    isTabletScreen,
    isCompactHeaderCart,
    isSticky,
}) => {
    /**
     * XNG-Changes
     */
    const [isMenuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        if (shouldLoadCart) {
            loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false });
        }
    }, [shouldLoadCart]);

    const styles = useMergeStyles("cartLink", cartLinkStyles);

    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay;
    }

    return (
        <FloatingMenuTooltip
            onOpenChange={open => setMenuOpen(open)}
            placement="bottom-end"
            triggerComponent={
                <HeaderClickable
                    isOpen={isMenuOpen}
                    href={cartUrl}
                    data-test-selector="cartLink"
                    aria-label={translate("Mini-Cart")}
                    extendedStyles={styles.headerClickable}
                    additionalElement={
                        cartItemsCount > 0 ? (
                            <Typography {...styles.count} {...{ isCompactHeader: isCompactHeaderCart }}>
                                {cartItemsCount}
                            </Typography>
                        ) : null
                    }
                    isCompactHeader={isCompactHeaderCart}
                >
                    <Typography {...styles.subtitle} {...{ isCompactHeader: isCompactHeaderCart }}>
                        {translate("Items ({0})", `${cartItemsCount}`)}
                    </Typography>
                    {orderSubTotalDisplay || `${currency}--`}
                </HeaderClickable>
            }
            enabled={!isTabletScreen && !isCartPage}
            offset={isSticky ? DEFAULT_STEINER_LARGE_STICKY_OFFSET : DEFAULT_STEINER_OFFSET}
            extendedStyles={styles.floatingTooltip}
        >
            <MiniCart />
        </FloatingMenuTooltip>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CartLink)),
    definition: {
        displayName: "Cart",
        icon: "cart-shopping",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
                isVisible: _ => false,
            },
            {
                name: fields.icon,
                displayName: "Shopping Cart Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },
                    {
                        displayName: "Bag",
                        value: "bag",
                    },
                    {
                        displayName: "Cart",
                        value: "cart",
                    },
                    {
                        displayName: "Truck",
                        value: "truck",
                    },
                ],
                defaultValue: "cart",
                fieldType: "General",
                sortOrder: 2,
                isVisible: item => false,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
